import { SelectChangeEvent } from '@mui/material';
import { SortChangedEvent } from 'ag-grid-community';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../context';
import { PaginatedResponse, PaginationParams, SortParams } from '../types';
import { useDebounce } from './debounce';

const INITIAL_PAGE_SIZE = 15;

interface QueryParams<F> {
  pagination: PaginationParams;
  filters: F;
  sort?: SortParams;
}

type QueryHook<F, D> = (
  params: QueryParams<F>,
  options: {
    skip?: boolean;
    refetchOnMountOrArgChange?: boolean | number;
    refetchOnFocus?: boolean;
  }
) => {
  data?: PaginatedResponse<D>;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
};

export const useInteractiveGrid = <F, D = unknown>(options: {
  hasAccess: boolean;
  prefetchFunction: (params: QueryParams<F>) => void;
  getDataQuery: QueryHook<F, D>;
  debounceOptions?: {
    enabled: boolean;
    delay?: number;
  };
  spinnerOptions?: {
    showSpinnerOnSort?: boolean;
    showSpinnerOnFilter?: boolean;
    showSpinnerOnPageChange?: boolean;
  };
  initialFilters?: Partial<F>;
  initialSort?: SortParams;
}) => {
  const {
    hasAccess,
    prefetchFunction,
    getDataQuery,
    debounceOptions,
    spinnerOptions = {},
    initialFilters,
    initialSort,
  } = options;

  const { showLoading, hideLoading } = useLoading();

  const debounceDelay = debounceOptions?.enabled ? (debounceOptions.delay ?? 300) : 0;

  const navigate = useNavigate();
  const [sort, setSort] = useState<SortParams | undefined>(initialSort);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [isReadyToFetch, setIsReadyToFetch] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Define state for pagination
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: INITIAL_PAGE_SIZE,
  });

  // Define state for filters and internal filters
  const [internalFilters, setInternalFilters] = useState<F>((initialFilters || {}) as F);

  // Use the useDebounce hook to debounce the internal filters
  const debouncedFilters = useDebounce<F>(
    internalFilters,
    debounceOptions?.enabled ? debounceDelay : 0
  );

  // Actual filters used for API calls
  const [filters, setFilters] = useState<F>((initialFilters || {}) as F);

  // Track manual loading state
  const [isManuallyLoading, setIsManuallyLoading] = useState(false);

  // Update filters when debounced filters change
  useEffect(() => {
    if (debounceOptions?.enabled) {
      setFilters(debouncedFilters);

      // Reset page to 1 when filters change
      setPagination(prev => ({
        ...prev,
        page: 1,
      }));
    }
  }, [debouncedFilters, debounceOptions?.enabled]);

  // Use RTK Query hook to fetch data with pagination and filters
  const queryResult = getDataQuery(
    {
      pagination,
      filters,
      sort,
    },
    {
      skip: !isReadyToFetch,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  // Extract all properties we need from the query result
  const response = queryResult.data;
  const isLoading = queryResult.isLoading;
  const isFetching = queryResult.isFetching;
  const error = queryResult.error;

  // This tracks the overall loading state - either from RTK Query or our manual actions
  const shouldShowLoadingIndicator = isLoading || isFetching || isManuallyLoading;

  // Control the loading spinner based on the overall loading state
  useEffect(() => {
    if (shouldShowLoadingIndicator) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [shouldShowLoadingIndicator, showLoading, hideLoading]);

  // Start manual loading for an operation and return a function to end it
  const startManualLoading = useCallback(() => {
    setIsManuallyLoading(true);

    // Return a function that can be called to end the loading state
    return () => {
      setIsManuallyLoading(false);
    };
  }, []);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (spinnerOptions?.showSpinnerOnPageChange) {
      startManualLoading();
    }

    setPagination(prev => ({
      ...prev,
      page: newPage,
    }));
  };

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent) => {
    if (spinnerOptions?.showSpinnerOnPageChange) {
      startManualLoading();
    }

    const newPageSize = parseInt(event.target.value, 10);
    setPagination({
      page: 1, // Reset to first page when changing page size
      pageSize: newPageSize,
    });
  };

  // Handle filter changes - use type-safe approach without any
  const handleFilterChange = <K extends keyof F>(field: K, value: F[K]) => {
    if (spinnerOptions?.showSpinnerOnFilter) {
      startManualLoading();
    }

    // Helper function to update filters when value is empty or null
    const removeFilterField = (currentFilters: F) => {
      const newFilters = { ...currentFilters };
      const { [field]: _, ...rest } = newFilters as Partial<Record<keyof F, unknown>>;
      return rest as F;
    };

    // Helper function to update filters with new value
    const updateFilterField = (currentFilters: F, newValue: F[K]) => {
      return { ...currentFilters, [field]: newValue } as F;
    };

    // Helper function to reset pagination to page 1
    const resetPagination = () => {
      setPagination(prev => ({
        ...prev,
        page: 1,
      }));
    };

    const isEmptyValue = value === '' || value === undefined || value === null;

    if (debounceOptions?.enabled) {
      // Update internal filters for debouncing
      setInternalFilters(
        isEmptyValue
          ? removeFilterField(internalFilters)
          : updateFilterField(internalFilters, value)
      );
    } else {
      // For non-debounced implementation, update filters directly
      setFilters(isEmptyValue ? removeFilterField(filters) : updateFilterField(filters, value));

      // Reset page to 1 when filters change
      resetPagination();
    }
  };

  // Handle sort changes
  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      if (spinnerOptions?.showSpinnerOnSort) {
        startManualLoading();
      }

      const columnState = event.api.getColumnState();
      const sortedColumn = columnState.find(column => column.sort);

      if (!sortedColumn) {
        setSort(undefined);
      } else if (sortedColumn) {
        // Set sort parameters based on the sorted column
        setSort({
          field: sortedColumn.colId,
          direction: sortedColumn.sort as 'asc' | 'desc',
        });
      }

      // Reset to first page when sort changes
      setPagination(prev => ({
        ...prev,
        page: 1,
      }));
    },
    [spinnerOptions?.showSpinnerOnSort, startManualLoading]
  );

  // Clear all filters - reset to initial filters (defaults like "Active" status)
  // This resets Status filter back to "Active" and clears other filters
  const handleClearFilters = () => {
    if (spinnerOptions?.showSpinnerOnFilter) {
      startManualLoading();
    }

    // Reset to initial filters (defaults) - e.g., Status filter back to "Active"
    const resetFilters = (initialFilters || {}) as F;

    if (debounceOptions?.enabled) {
      setInternalFilters(resetFilters);
    }
    setFilters(resetFilters);
    // Reset page to 1 when filters are cleared
    setPagination(prev => ({
      ...prev,
      page: 1,
    }));
  };

  // When data finishes loading, turn off manual loading if it was enabled
  useEffect(() => {
    if (!isLoading && !isFetching && isManuallyLoading) {
      setIsManuallyLoading(false);
    }
  }, [isLoading, isFetching, isManuallyLoading]);

  // Determine optimal page size based on container height - now responsive to size changes
  const calculateOptimalPageSize = useCallback(() => {
    if (gridContainerRef.current) {
      const containerHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 31;
      const headerHeight = 31;
      const paddingBuffer = 20;

      const availableHeight = containerHeight - headerHeight - paddingBuffer;
      const optimalRows = Math.floor(availableHeight / rowHeight);

      let newPageSize = INITIAL_PAGE_SIZE;

      if (optimalRows <= 10) {
        newPageSize = 10;
      } else if (optimalRows <= 15) {
        newPageSize = 15;
      } else if (optimalRows <= 20) {
        newPageSize = 20;
      } else if (optimalRows <= 25) {
        newPageSize = 25;
      } else if (optimalRows <= 30) {
        newPageSize = 30;
      } else {
        newPageSize = 50;
      }

      setPagination(prev => {
        if (prev.pageSize !== newPageSize) {
          return {
            ...prev,
            pageSize: newPageSize,
            page: 1, // Reset to first page when page size changes
          };
        }
        return prev;
      });

      if (!isReadyToFetch) {
        setIsReadyToFetch(true);
      }
    }
  }, [isReadyToFetch]);

  // Initial calculation and setup resize observer for responsive behavior
  useEffect(() => {
    calculateOptimalPageSize();

    // Add resize observer to recalculate when container size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateOptimalPageSize();
    });

    if (gridContainerRef.current) {
      resizeObserver.observe(gridContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateOptimalPageSize]);

  // Check access
  useEffect(() => {
    if (!hasAccess) {
      navigate('/');
    }
  }, [hasAccess, navigate]);

  // Prefetch next page
  const handleNextPageHover = useCallback(() => {
    if (response?.totalPages && pagination.page < response.totalPages) {
      prefetchFunction({
        pagination: { ...pagination, page: pagination.page + 1 },
        filters,
        sort,
      });
    }
  }, [pagination, response, filters, sort, prefetchFunction]);

  // Prefetch the next page when current page data is loaded
  useEffect(() => {
    if (response?.totalPages && pagination.page < response.totalPages && !isLoading) {
      prefetchFunction({
        pagination: { ...pagination, page: pagination.page + 1 },
        filters,
        sort,
      });
    }
  }, [pagination, pagination.page, response, prefetchFunction, filters, sort, isLoading]);

  // Make sure to hide loading on unmount if it was manually started
  useEffect(() => {
    return () => {
      if (isManuallyLoading) {
        hideLoading();
      }
    };
  }, [isManuallyLoading, hideLoading]);

  return {
    navigate,
    sort,
    gridContainerRef,
    pagination,
    filters: debounceOptions?.enabled ? internalFilters : filters,
    filtersExpanded,
    setFiltersExpanded,
    response,
    isLoading: shouldShowLoadingIndicator,
    error,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    onSortChanged,
    handleClearFilters,
    handleNextPageHover,
    hasAccess,
  };
};
